import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { EtapaCiclo, StatusLoteSof } from '../types';
import { MOCK_CICLO, MOCK_EMPENHOS, MOCK_SOF } from '../dados-simulados';

export interface FiltroCiclo {
  etapa: EtapaCiclo;
  mes?: string;
  /** Natureza do credenciado (PF/PJ) — omitido mostra as duas. */
  natureza?: 'PF' | 'PJ';
  page: number;
  perPage: number;
}

const MAPA_ETAPA_STATUS: Record<EtapaCiclo, string[]> = {
  CADASTRO: ['APROVADA'],
  EMPENHO: ['APROVADA'],
  ATESTE: ['EMPENHADA', 'EXPORTADA_SOF'],
  LIQUIDACAO: ['EMPENHADA', 'EXPORTADA_SOF'],
  AUTORIZACAO: ['LIQUIDADA'],
};

/** FIN-012: pipeline financeiro (Cadastro → Empenho → Ateste → Liquidação → Autorização). */
export function useCicloFinanceiro(filtro: FiltroCiclo) {
  return useQuery({
    queryKey: ['financeiro', 'ciclo', filtro],
    queryFn: async () => {
      const statusPermitidos = MAPA_ETAPA_STATUS[filtro.etapa] || [];
      let filtrados = MOCK_CICLO.filter((c) => statusPermitidos.includes(c.status));

      if (filtro.mes) {
        filtrados = filtrados.filter((f) => f.mesReferencia.startsWith(filtro.mes!));
      }
      if (filtro.natureza) {
        filtrados = filtrados.filter((f) => f.natureza === filtro.natureza);
      }

      return {
        dados: filtrados,
        total: filtrados.length,
        page: filtro.page,
        perPage: filtro.perPage,
      };
    },
    placeholderData: (dadosAnteriores) => dadosAnteriores,
  });
}

export interface FiltroLotesSof {
  mes?: string;
  status?: StatusLoteSof;
  page: number;
  perPage: number;
}

/** FIN-030/031: lotes de empenho exportados para a SOF. */
export function useLotesSof(filtro: FiltroLotesSof) {
  return useQuery({
    queryKey: ['financeiro', 'sof', 'lotes', filtro],
    queryFn: async () => {
      let filtrados = [...MOCK_SOF];
      if (filtro.mes) {
        filtrados = filtrados.filter((f) => f.mesReferencia.startsWith(filtro.mes!));
      }
      if (filtro.status) {
        filtrados = filtrados.filter((f) => f.status === filtro.status);
      }
      return {
        dados: filtrados,
        total: filtrados.length,
        page: filtro.page,
        perPage: filtro.perPage,
      };
    },
    placeholderData: (dadosAnteriores) => dadosAnteriores,
  });
}

/** Cartões de resumo do hub SOF: quantidade de lotes e valor total da competência. */
export function useResumoLotesSof(mes?: string) {
  return useQuery({
    queryKey: ['financeiro', 'sof', 'lotes', 'resumo', mes ?? null],
    queryFn: async () => {
      let filtrados = [...MOCK_SOF];
      if (mes) {
        filtrados = filtrados.filter((f) => f.mesReferencia.startsWith(mes));
      }

      const sum = filtrados.reduce((acc, curr) => acc + parseFloat(curr.valorTotal), 0);
      return {
        totalLotes: filtrados.length,
        valorTotal: sum.toFixed(2),
      };
    },
  });
}

/** Notas de empenho vinculadas aos lotes SOF — aba "Empenhos" do hub. */
export function useEmpenhosSof() {
  return useQuery({
    queryKey: ['financeiro', 'sof', 'empenhos'],
    queryFn: async () => [...MOCK_EMPENHOS],
  });
}

/** Marca um lote como enviado à SOF — aba "Exportar" do hub (gera o arquivo
 * e registra o envio; a confirmação de retorno da SOF é manual, como no
 * protótipo, porque o canal oficial de retorno ainda não existe). */
export function useExportarLoteSof() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (loteId: string) => {
      await new Promise((resolve) => setTimeout(resolve, 600));
      const lote = MOCK_SOF.find((l) => l.id === loteId);
      if (lote) {
        lote.status = 'ENVIADO_SOF';
        lote.enviadoSofEm = new Date().toISOString();
      }
      return { ok: true as const };
    },
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: ['financeiro', 'sof', 'lotes'] }),
  });
}
